import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import SmartImage from "../../lib/UIBookBrd/SmartImage";
import {container, displayHeading, down, eyebrow, focusRing, hideScrollbar} from "../../Styles/media";

const Page = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding-bottom: clamp(56px, 8vw, 110px);
`

const TopBar = styled.div`
    ${container};
    display: flex;
    align-items: center;
    gap: 14px;
    padding-top: clamp(20px, 3vw, 34px);
    padding-bottom: clamp(18px, 2.5vw, 28px);
`

const BackButton = styled.button`
    ${focusRing};
    display: inline-flex;
    align-items: center;
    gap: 10px;
    height: 38px;
    padding: 0 16px 0 13px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.pill};
    font-size: 13px;
    font-weight: 600;
    color: ${({theme}) => theme.colorMuted};
    transition: color 220ms ${({theme}) => theme.ease.out}, border-color 220ms ${({theme}) => theme.ease.out};

    &:hover {
        color: ${({theme}) => theme.color};
        border-color: ${({theme}) => theme.lineStrong};
    }
`

const BackIcon = styled(FontAwesomeIcon)`
    width: 12px;
    height: 12px;
`

const Breadcrumb = styled.span`
    ${eyebrow};
    font-size: 10.5px;

    ${down('sm')} {
        display: none;
    }
`

const Layout = styled.div`
    ${container};
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
    gap: clamp(20px, 3vw, 48px);
    align-items: start;

    ${down('lg')} {
        grid-template-columns: minmax(0, 1fr);
    }
`

/* ------------------------------------------------------------------ gallery */

const Gallery = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const Frame = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.lg};
    background: radial-gradient(70% 70% at 50% 40%, rgba(255, 255, 255, 0.07), transparent 70%),
    ${({theme}) => theme.surface};
`

const FrameImage = styled(SmartImage)`
    position: absolute;
    inset: 0;
    padding: clamp(18px, 3vw, 44px);
`

const FrameNav = styled.button<{ $side: 'left' | 'right' }>`
    ${focusRing};
    position: absolute;
    top: 50%;
    ${({$side}) => ($side === 'left' ? 'left: 14px;' : 'right: 14px;')};
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: 50%;
    background-color: rgba(8, 8, 10, 0.66);
    backdrop-filter: blur(8px);
    color: ${({theme}) => theme.color};
    transition: background-color 220ms ${({theme}) => theme.ease.out}, border-color 220ms ${({theme}) => theme.ease.out};

    &:hover {
        background-color: ${({theme}) => theme.accent};
        border-color: transparent;
        color: ${({theme}) => theme.accentContrast};
    }
`

const FrameNavIcon = styled(FontAwesomeIcon)`
    width: 14px;
    height: 14px;
`

const FrameCounter = styled.span`
    position: absolute;
    bottom: 14px;
    right: 16px;
    font-family: ${({theme}) => theme.font.mono};
    font-size: 11.5px;
    letter-spacing: 0.1em;
    color: ${({theme}) => theme.colorMuted};
`

const Thumbs = styled.div`
    ${hideScrollbar};
    display: flex;
    gap: 10px;
    overflow-x: auto;
`

const Thumb = styled.button<{ $active: boolean }>`
    ${focusRing};
    position: relative;
    flex: 0 0 auto;
    width: 92px;
    height: 68px;
    padding: 7px;
    overflow: hidden;
    border: 1px solid ${({theme, $active}) => ($active ? theme.accent : theme.line)};
    border-radius: ${({theme}) => theme.radius.sm};
    background-color: rgba(255, 255, 255, 0.03);
    transition: border-color 220ms ${({theme}) => theme.ease.out}, opacity 220ms ${({theme}) => theme.ease.out};
    opacity: ${({$active}) => ($active ? 1 : 0.6)};

    &:hover {
        opacity: 1;
    }
`

const ThumbImage = styled(SmartImage)`
    width: 100%;
    height: 100%;
`

/* -------------------------------------------------------------------- panel */

const Panel = styled.aside`
    position: sticky;
    top: calc(${({theme}) => theme.header.height}px + 24px);
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: clamp(20px, 2.6vw, 32px);
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.lg};
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.014));

    ${down('lg')} {
        position: static;
    }
`

const Producer = styled.span`
    ${eyebrow};
`

const Title = styled.h1`
    ${displayHeading};
    font-size: clamp(28px, 3.6vw, 44px);
`

const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`

const Chip = styled.span<{ $tone?: 'default' | 'accent' }>`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 6px 12px;
    border: 1px solid ${({theme, $tone}) => ($tone === 'accent' ? theme.accentLine : theme.line)};
    border-radius: ${({theme}) => theme.radius.pill};
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({theme, $tone}) => ($tone === 'accent' ? theme.color : theme.colorMuted)};
    background-color: ${({theme, $tone}) => ($tone === 'accent' ? theme.accentSoft : 'transparent')};
`

const ChipIcon = styled(FontAwesomeIcon)`
    width: 11px;
    height: 11px;
`

const PriceRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding-top: 4px;
`

const Price = styled.span`
    font-size: clamp(30px, 3.6vw, 42px);
    font-weight: 700;
    letter-spacing: -0.03em;
    color: ${({theme}) => theme.color};
`

const PriceUnit = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${({theme}) => theme.colorMuted};
`

const Description = styled.p`
    font-size: 15px;
    line-height: 1.7;
    color: ${({theme}) => theme.colorMuted};
`

const Actions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 4px;
`

const ActionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`

const Note = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding-top: 14px;
    border-top: 1px solid ${({theme}) => theme.lineFaint};
    font-size: 12.5px;
    line-height: 1.55;
    color: ${({theme}) => theme.colorFaint};
`

const NoteIcon = styled(FontAwesomeIcon)`
    width: 13px;
    height: 13px;
    margin-top: 3px;
    flex-shrink: 0;
    color: ${({theme}) => theme.colorFaint};
`

/* --------------------------------------------------------------------- specs */

const Section = styled.section`
    ${container};
    padding-top: clamp(48px, 7vw, 96px);
`

const SectionHead = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 24px;
    margin-bottom: clamp(20px, 3vw, 32px);
`

const SectionTitle = styled.h2`
    ${displayHeading};
    font-size: clamp(24px, 3vw, 38px);
`

const SpecList = styled.ul`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
    gap: 2px 32px;
    list-style: none;

    ${down('sm')} {
        grid-template-columns: minmax(0, 1fr);
    }
`

const SpecItem = styled.li`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 13px 0;
    border-bottom: 1px solid ${({theme}) => theme.lineFaint};
    font-size: 14.5px;
    line-height: 1.6;
    color: ${({theme}) => theme.colorMuted};
`

const SpecIcon = styled(FontAwesomeIcon)`
    width: 12px;
    height: 12px;
    margin-top: 5px;
    flex-shrink: 0;
    color: ${({theme}) => theme.accent};
`

const SimilarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(258px, 100%), 1fr));
    gap: clamp(12px, 1.5vw, 20px);

    ${down('xs')} {
        grid-template-columns: minmax(0, 1fr);
    }
`

/* ----------------------------------------------------------------- not found */

const NotFound = styled.div`
    ${container};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: clamp(70px, 12vw, 150px) 0;
    text-align: center;
`

const NotFoundIcon = styled(FontAwesomeIcon)`
    width: 32px;
    height: 32px;
    color: ${({theme}) => theme.colorFaint};
`

const NotFoundTitle = styled.h1`
    ${displayHeading};
    font-size: clamp(26px, 3.4vw, 40px);
`

const NotFoundText = styled.p`
    max-width: 44ch;
    font-size: 15px;
    color: ${({theme}) => theme.colorMuted};
`

export const Styled = {
    Page,
    TopBar,
    BackButton,
    BackIcon,
    Breadcrumb,
    Layout,

    Gallery,
    Frame,
    FrameImage,
    FrameNav,
    FrameNavIcon,
    FrameCounter,
    Thumbs,
    Thumb,
    ThumbImage,

    Panel,
    Producer,
    Title,
    Chips,
    Chip,
    ChipIcon,
    PriceRow,
    Price,
    PriceUnit,
    Description,
    Actions,
    ActionRow,
    Note,
    NoteIcon,

    Section,
    SectionHead,
    SectionTitle,
    SpecList,
    SpecItem,
    SpecIcon,
    SimilarGrid,

    NotFound,
    NotFoundIcon,
    NotFoundTitle,
    NotFoundText
}
